/**
 * Regelbauweise envelope score helpers (land-v5.0).
 *
 * Zones without utilization ratios are scored from official dimensional rules:
 * buildable footprint after setbacks x authorized height, compared to the
 * existing built volume (VBr). See the data-team handoff (PR #1562) and the
 * approved design in mockups/zoning-envelope-score/idea-b-refinement.html.
 */

export type EnvelopeSource = 'manual' | 'geoportal' | 'llm'

export type EnvelopeState =
  | 'potential'
  | 'at_capacity'
  | 'grandfathered'
  | 'unbuildable_alone'

/** The subset of Land fields the envelope helpers need. */
export interface EnvelopeLandFields {
  indice_used?: string | null
  volume_envelope_authorized?: number | null
  volume_envelope_difference?: number | null
  buildable_area_setback?: number | null
  buildable_area_effective?: number | null
}

const ENVELOPE_PREFIX = 'envelope_'
const ENVELOPE_SOURCES: EnvelopeSource[] = ['manual', 'geoportal', 'llm']

/** True when the land's score was produced by the envelope branch. */
export const isEnvelopeScored = (indiceUsed?: string | null): boolean =>
  typeof indiceUsed === 'string' && indiceUsed.startsWith(ENVELOPE_PREFIX)

/**
 * Which envelope computation produced the score:
 * - 'setbacks': dedicated small/big setback pair
 * - 'distance_limit': distance-limit fallback (min value as small setback)
 * - 'contiguous': contiguous construction arrangement (ordre contigu) —
 *   setbacks apply only to non-attached boundaries
 * Unknown middles return null (future pipeline variants render generically).
 */
export type EnvelopeVariant = 'setbacks' | 'distance_limit' | 'contiguous'

export const getEnvelopeVariant = (
  indiceUsed?: string | null
): EnvelopeVariant | null => {
  if (!isEnvelopeScored(indiceUsed)) return null
  const middle = (indiceUsed as string)
    .slice(ENVELOPE_PREFIX.length)
    .replace(/_(manual|geoportal|llm)$/, '')
  return middle === 'setbacks' ||
    middle === 'distance_limit' ||
    middle === 'contiguous'
    ? (middle as EnvelopeVariant)
    : null
}

/**
 * Source suffix of an envelope indice_used
 * (envelope_setbacks_llm -> 'llm'). Maps to the existing badge system.
 */
export const getEnvelopeSource = (
  indiceUsed?: string | null
): EnvelopeSource | null => {
  if (!isEnvelopeScored(indiceUsed)) return null
  const suffix = (indiceUsed as string).split('_').pop() || ''
  return ENVELOPE_SOURCES.includes(suffix as EnvelopeSource)
    ? (suffix as EnvelopeSource)
    : null
}

/**
 * Classifies a populated envelope. The check order is load-bearing:
 * `buildable_area_setback === 0` also has a negative difference (-vbr), so
 * unbuildable-alone MUST be tested before grandfathered.
 */
export const getEnvelopeState = (
  land: EnvelopeLandFields
): EnvelopeState | null => {
  if (!isEnvelopeScored(land.indice_used)) return null
  const authorized = land.volume_envelope_authorized
  const difference = land.volume_envelope_difference
  const footprint = land.buildable_area_setback
  // Branch refused: fields are null
  if (authorized == null || difference == null || footprint == null) return null
  if (footprint === 0) return 'unbuildable_alone'
  if (difference < 0) return 'grandfathered'
  if (difference === 0) return 'at_capacity'
  return 'potential'
}

/**
 * The envelope volume is linear in height, so overriding the authorized
 * height can be recomputed exactly: effective footprint x height.
 */
export const computeEnvelopeAuthorizedVolume = (
  effectiveAreaM2: number,
  heightM: number
): number => {
  if (!(effectiveAreaM2 > 0) || !(heightM > 0)) return 0
  return Math.round(effectiveAreaM2 * heightM)
}

/** Pipeline formula: round(max(0, 1 - vbr / authorized) * 100). */
export const computeEnvelopeUnderExploitedScore = (
  vbr: number,
  authorizedVolume: number
): number => {
  if (!(authorizedVolume > 0)) return 0
  return Math.round(Math.max(0, (1 - vbr / authorizedVolume) * 100))
}
